import React, { useMemo, useState } from "react";
import useSWR from "swr";
import { Button, Form, Modal } from "react-bootstrap";
import { FaCalendarAlt, FaChartLine } from "react-icons/fa";
import { ORIGIN } from "../utils/configs";
import { processCommand } from "../utils/utils";
import AssetLabel from "../components/common/AssetLabel";
import {
  TRADE_COMMENT_TEMPLATE,
  activeTradePnl,
  buildReopenTradeData,
  buildTradeEvent,
  closePositionCommand,
  encodeCommandText,
  formatPnl,
  formatTradePrice,
  isActiveTrade,
  potentialStopLossAmount,
  priceProtectLabel,
  tradeAmountText,
  tradeEntryLabel,
  tpLabel,
  tradePnl
} from "./tradeUtils";
import "./MyTrades.css";

const fetcher = (...args) => fetch(...args, { credentials: "same-origin", cache: "no-store" }).then((res) => res.json());

const HISTORY_RANGE_STORAGE_KEY = "myTradesHistoryRange";
const HISTORY_RANGES = [
  ["Last 7 days", "last7Days"],
  ["Today", "today"],
  ["This week", "thisWeek"],
  ["Last week", "lastWeek"],
  ["This month", "thisMonth"],
  ["Last month", "lastMonth"],
  ["Last 30 days", "last30Days"],
  ["All", "all"]
];
const PNL_PERIODS = [
  ["Today", "today"],
  ["This week", "thisWeek"],
  ["Last week", "lastWeek"],
  ["This month", "thisMonth"],
  ["Last month", "lastMonth"],
  ["Last 30 days", "last30Days"]
];

function normalizedSymbol(value) {
  return String(value || "").toUpperCase();
}

function samePositionSide(itemSide, tradeSide, positionAmt) {
  if (!tradeSide) return true;
  if (!itemSide || itemSide === tradeSide) return true;
  if (itemSide === "BOTH") {
    const amt = Number(positionAmt);
    if (!Number.isFinite(amt)) return true;
    return tradeSide === "LONG" ? amt > 0 : amt < 0;
  }
  return false;
}

function toMillis(value) {
  if (value == null) return null;
  const ts = Number(value);
  if (!Number.isFinite(ts) || ts <= 0) return null;
  return ts < 1000000000000 ? ts * 1000 : ts;
}

function readHistoryRange() {
  try {
    const stored = localStorage.getItem(HISTORY_RANGE_STORAGE_KEY);
    return HISTORY_RANGES.some(([, key]) => key === stored) ? stored : "last7Days";
  } catch (error) {
    return "last7Days";
  }
}

function startOfDay(value) {
  const result = new Date(value);
  result.setHours(0, 0, 0, 0);
  return result.getTime();
}

function startOfWeek(value) {
  const result = new Date(startOfDay(value));
  const day = result.getDay();
  result.setDate(result.getDate() - (day === 0 ? 6 : day - 1));
  return result.getTime();
}

function historyRangeBounds(range, now = Date.now()) {
  const current = new Date(now);
  if (range === "all") return { start: Number.NEGATIVE_INFINITY, end: Number.POSITIVE_INFINITY };
  if (range === "today") return { start: startOfDay(now), end: Number.POSITIVE_INFINITY };
  if (range === "thisWeek") return { start: startOfWeek(now), end: Number.POSITIVE_INFINITY };
  if (range === "lastWeek") {
    const thisWeek = startOfWeek(now);
    return { start: thisWeek - (7 * 24 * 60 * 60 * 1000), end: thisWeek };
  }
  if (range === "thisMonth") {
    return { start: new Date(current.getFullYear(), current.getMonth(), 1).getTime(), end: Number.POSITIVE_INFINITY };
  }
  if (range === "lastMonth") {
    return {
      start: new Date(current.getFullYear(), current.getMonth() - 1, 1).getTime(),
      end: new Date(current.getFullYear(), current.getMonth(), 1).getTime()
    };
  }
  const days = range === "last30Days" ? 30 : 7;
  return { start: now - (days * 24 * 60 * 60 * 1000), end: Number.POSITIVE_INFINITY };
}

function isInHistoryRange(timestamp, range) {
  if (range === "all") return true;
  if (timestamp == null) return false;
  const { start, end } = historyRangeBounds(range);
  return timestamp >= start && timestamp < end;
}

function latestItemMillis(items) {
  return (items || []).reduce((latest, item) => Math.max(latest, positionItemMillis(item) || 0), 0) || null;
}

function tradeHistoryMillis(item) {
  return toMillis(item?.trade?.closedAt)
    || latestItemMillis(item?.positionExecutions)
    || latestItemMillis(item?.positionFills)
    || toMillis(item?.trade?.entryFilledAt)
    || toMillis(item?.trade?.submittedAt);
}

function catchHistoryMillis(catchTrade) {
  return toMillis(catchTrade?.completedAt) || toMillis(catchTrade?.submittedAt);
}

function tradeStartMillis(trade) {
  return toMillis(trade?.submittedAt || trade?.entryFilledAt);
}

function tradeEndMillis(trade) {
  const closedAt = toMillis(trade?.closedAt);
  if (closedAt != null) return closedAt;
  return isActiveTrade(trade) ? null : toMillis(trade?.entryFilledAt);
}

function positionItemMillis(item) {
  return toMillis(item?.updatedAt || item?.time || item?.createdAt);
}

function isWithinTradeWindow(item, trade) {
  const itemTime = positionItemMillis(item);
  if (itemTime == null) return true;
  const start = tradeStartMillis(trade);
  if (start != null && itemTime < start) return false;
  const end = tradeEndMillis(trade);
  if (end != null && itemTime > end + 60000) return false;
  return true;
}

function positionLaneForTrade(positionJournal, trade) {
  const symbol = normalizedSymbol(trade?.symbol);
  return (positionJournal?.lanes || []).find((lane) => (
    normalizedSymbol(lane.symbol) === symbol
      && samePositionSide(String(lane.positionSide || "").toUpperCase(), trade?.side, lane.positionAmt)
  ));
}

function positionOrdersForTrade(positionJournal, trade) {
  const symbol = normalizedSymbol(trade?.symbol);
  return (positionJournal?.orders || []).filter((order) => (
    normalizedSymbol(order.symbol) === symbol
      && samePositionSide(String(order.positionSide || "").toUpperCase(), trade?.side)
  ));
}

function positionFillsForTrade(positionJournal, trade) {
  const symbol = normalizedSymbol(trade?.symbol);
  return (positionJournal?.fills || []).filter((fill) => (
    normalizedSymbol(fill.symbol) === symbol
      && samePositionSide(String(fill.positionSide || "").toUpperCase(), trade?.side)
      && isWithinTradeWindow(fill, trade)
  ));
}

function positionExecutionsForTrade(positionJournal, trade) {
  const symbol = normalizedSymbol(trade?.symbol);
  return (positionJournal?.executions || []).filter((execution) => (
    normalizedSymbol(execution.symbol) === symbol
      && samePositionSide(String(execution.positionSide || "").toUpperCase(), trade?.side)
      && isWithinTradeWindow(execution, trade)
  ));
}

function activeStopOrder(orders) {
  return (orders || []).find((order) => {
    const type = String(order?.type || "").toUpperCase();
    return type.includes("STOP") || order?.stopPrice != null;
  });
}

function exchangeStopRiskAmount(trade, lane, orders) {
  const entry = Number(lane?.entryPrice || trade?.actualEntry || trade?.plannedEntry);
  const quantity = Math.abs(Number(lane?.positionAmt));
  const stop = Number(activeStopOrder(orders)?.stopPrice || trade?.stopLoss);
  if (!Number.isFinite(entry) || !Number.isFinite(quantity) || !Number.isFinite(stop) || quantity <= 0) {
    return null;
  }
  return Math.abs((stop - entry) * quantity);
}

function exchangePositionText(lane) {
  if (!lane) return null;
  const quantity = Math.abs(Number(lane.positionAmt));
  const notional = Math.abs(Number(lane.notional));
  const parts = [];
  if (Number.isFinite(quantity)) parts.push(`${quantity.toFixed(quantity >= 10 ? 3 : 5).replace(/\.?0+$/, "")} qty`);
  if (Number.isFinite(notional)) parts.push(`${notional.toFixed(2)} USDT`);
  if (lane.leverage) parts.push(`x${lane.leverage}`);
  return parts.join(" / ");
}

function executionPnlValue(item) {
  const net = Number(item?.netPnl);
  if (Number.isFinite(net)) return net;
  const pnl = Number(item?.realizedPnl);
  const commission = Number(item?.commission);
  return (Number.isFinite(pnl) ? pnl : 0) - (Number.isFinite(commission) ? Math.abs(commission) : 0);
}

function executionPnlText(item) {
  const pnl = executionPnlValue(item);
  const classification = String(item?.classification || "").toUpperCase();
  if (!Number.isFinite(pnl) || Math.abs(pnl) < 0.005 || !["REDUCE", "CLOSE"].includes(classification)) {
    return "";
  }
  return ` ${formatPnl(pnl)}`;
}

function fillGroupKey(fill) {
  return fill?.orderId || fill?.tradeId || `${fill?.classification}-${fill?.time}-${fill?.price}`;
}

function groupedFills(fills) {
  const groupsByKey = new Map();
  (fills || []).forEach((fill) => {
    const key = fillGroupKey(fill);
    const existing = groupsByKey.get(key) || {
      ...fill,
      quantity: 0,
      quoteQuantity: 0,
      realizedPnl: 0,
      commission: 0,
      fillCount: 0
    };
    const qty = Number(fill.quantity);
    const quote = Number(fill.quoteQuantity);
    const pnl = Number(fill.realizedPnl);
    const commission = Number(fill.commission);
    existing.quantity += Number.isFinite(qty) ? qty : 0;
    existing.quoteQuantity += Number.isFinite(quote) ? quote : 0;
    existing.realizedPnl += Number.isFinite(pnl) ? pnl : 0;
    existing.commission += Number.isFinite(commission) ? commission : 0;
    existing.fillCount += 1;
    existing.time = Math.max(Number(existing.time || 0), Number(fill.time || 0));
    if (existing.quantity > 0 && existing.quoteQuantity > 0) {
      existing.price = existing.quoteQuantity / existing.quantity;
    }
    groupsByKey.set(key, existing);
  });
  return Array.from(groupsByKey.values()).sort((a, b) => Number(a.time || 0) - Number(b.time || 0));
}

function exchangeRealizedPnl(executions, fills) {
  const closingExecutions = (executions || []).filter((execution) => ["REDUCE", "CLOSE"].includes(String(execution?.classification || "").toUpperCase()));
  if (closingExecutions.length > 0) {
    return closingExecutions.reduce((sum, execution) => sum + executionPnlValue(execution), 0);
  }
  const closingFills = groupedFills(fills).filter((fill) => ["REDUCE", "CLOSE"].includes(String(fill?.classification || "").toUpperCase()));
  if (closingFills.length === 0) return null;
  return closingFills.reduce((sum, fill) => {
    return sum + executionPnlValue(fill);
  }, 0);
}

function displayExecutions(executions, fills) {
  const primaryRows = (executions || []).filter((execution) => !["OPEN"].includes(String(execution?.classification || "").toUpperCase()));
  if (primaryRows.length > 0) {
    return primaryRows.sort((a, b) => Number(a.updatedAt || a.createdAt || 0) - Number(b.updatedAt || b.createdAt || 0));
  }
  return groupedFills(fills).filter((fill) => !["OPEN"].includes(String(fill?.classification || "").toUpperCase()));
}

function executionRowPrice(item) {
  return item?.avgPrice ?? item?.price;
}

function weightedClosePrice(executions, fills, actualClose) {
  const closingExecutions = (executions || []).filter((execution) => ["REDUCE", "CLOSE"].includes(String(execution?.classification || "").toUpperCase()));
  const closingItems = closingExecutions.length > 0
    ? closingExecutions
    : groupedFills(fills).filter((fill) => ["REDUCE", "CLOSE"].includes(String(fill?.classification || "").toUpperCase()));

  let totalQuantity = 0;
  let totalQuote = 0;
  closingItems.forEach((item) => {
    const quantity = Math.abs(Number(item?.executedQuantity ?? item?.quantity));
    const quoteQuantity = Math.abs(Number(item?.quoteQuantity));
    const price = Number(executionRowPrice(item));
    if (!Number.isFinite(quantity) || quantity <= 0) return;

    if (Number.isFinite(quoteQuantity) && quoteQuantity > 0) {
      totalQuote += quoteQuantity;
    } else if (Number.isFinite(price) && price > 0) {
      totalQuote += quantity * price;
    } else {
      return;
    }
    totalQuantity += quantity;
  });

  if (totalQuantity > 0 && totalQuote > 0) {
    return totalQuote / totalQuantity;
  }
  const journalClose = Number(actualClose);
  return Number.isFinite(journalClose) && journalClose > 0 ? journalClose : null;
}

function executionRowKey(item) {
  return item.orderId || item.tradeId || `${item.classification}-${item.updatedAt || item.time}-${item.price}`;
}

function hasExecutedEntry(trade) {
  return trade?.actualEntry != null
    || toMillis(trade?.entryFilledAt) != null
    || Number(trade?.initialQuantity) > 0;
}

function PnlSummaryModal({ show, summary, onHide }) {
  return (
    <Modal show={show} onHide={onHide} centered contentClassName="pnlSummaryModal">
      <Modal.Header closeButton closeVariant="white">
        <Modal.Title>Binance futures net realized PnL</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {summary ? (
          <div className="pnlPeriodGrid">
            {PNL_PERIODS.map(([label, key]) => {
              const value = Number(summary[key]);
              const positive = Number.isFinite(value) && value >= 0;
              return (
                <div key={key} className="pnlPeriodItem">
                  <span>{label}</span>
                  <strong className={positive ? "pnlPeriodPositive" : "pnlPeriodNegative"}>
                    {Number.isFinite(value) ? formatPnl(value) : "Unavailable"}
                  </strong>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="pnlSummaryUnavailable">PnL is being synchronized from Binance.</div>
        )}
        <div className="pnlSummaryNote">
          Includes realized PnL, commissions, funding, and manually executed trades.
          {summary?.timezone ? ` Periods use ${summary.timezone} time.` : ""}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>Close</Button>
      </Modal.Footer>
    </Modal>
  );
}

function HistoryRangeModal({ show, value, onChange, onHide }) {
  return (
    <Modal show={show} onHide={onHide} centered contentClassName="historyRangeModal">
      <Modal.Header closeButton closeVariant="white">
        <Modal.Title>History period</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="historyRangeGrid">
          {HISTORY_RANGES.map(([label, key]) => (
            <button
              key={key}
              type="button"
              className={`historyRangeOption ${value === key ? "historyRangeOptionSelected" : ""}`}
              onClick={() => onChange(key)}
            >
              {label}
            </button>
          ))}
        </div>
      </Modal.Body>
    </Modal>
  );
}

function CatchCard({ catchTrade, openPopup, onRefresh }) {
  const attempts = [...(catchTrade?.attempts || [])]
    .sort((a, b) => Number(a?.attemptNumber || 0) - Number(b?.attemptNumber || 0));
  const latestAttempt = attempts[attempts.length - 1];
  const paused = catchTrade?.status === "PAUSED_ERROR";
  const waitingSession = catchTrade?.status === "WAITING_SESSION";

  const openCatchChart = () => {
    const event = latestAttempt
      ? buildTradeEvent(latestAttempt)
      : {
          cur: catchTrade.cur,
          name: `${catchTrade.cur} ${catchTrade.side} CATCH`,
          tf: `_${catchTrade.timeframe || "15M"}`,
          ts: catchTrade.submittedAt,
          focusLatest: Boolean(catchTrade.active),
          catchTrade
        };
    event.name = `${catchTrade.cur} ${catchTrade.side} CATCH ${catchTrade.status}`;
    event.tf = `_${catchTrade.timeframe || "15M"}`;
    event.catchTrade = catchTrade;
    openPopup({
      index: 0,
      sectionEvents: [event],
      sectionTitleWhenFromHistory: "My Trades",
      fullHistoryFlat: [event]
    });
  };

  const runCatchCommand = (command, question) => {
    if (question && !window.confirm(question)) return;
    processCommand({ command })
      .catch((error) => console.error("Catch command failed:", error));
    setTimeout(onRefresh, 1500);
  };

  return (
    <div className={`catchCard ${catchTrade.active ? "catchCardActive" : ""} ${(paused || waitingSession) ? "catchCardPaused" : ""}`}>
      <button type="button" className="tradeCardMain" onClick={openCatchChart}>
        <div className="tradeCardTop">
          <span className="tradeSymbol"><AssetLabel symbol={catchTrade.symbol} event={catchTrade} /></span>
          <span className={`tradeStatus ${catchTrade.active ? "tradeStatusActive" : ""}`}>{catchTrade.status}</span>
        </div>
        <div className="catchIdentity">{catchTrade.side} {catchTrade.timeframe} liquidity Catch</div>
        <div className="tradeRiskMeta">
          <span>Baseline {formatTradePrice(catchTrade.currentBaseline)}</span>
          <span>{catchTrade.value} USDT x{catchTrade.leverage}</span>
          <span>{priceProtectLabel(catchTrade)}</span>
          <span>Attempts {catchTrade.attemptsUsed}/{catchTrade.maxAttempts}</span>
        </div>
        <div className="tradeMeta">
          <span>Activate within {catchTrade.activationPercent}%</span>
          <span>SL {catchTrade.stopLossPercent}%</span>
          <span>TP1 {catchTrade.tp1Percent}%</span>
          <span>TP2 {catchTrade.tp2Hold ? "HOLD" : `${catchTrade.tp2Percent}%`}</span>
        </div>
        {catchTrade.candidateBaseline != null ? (
          <div className="catchCandidate">
            Candidate {formatTradePrice(catchTrade.candidateBaseline)} / {catchTrade.candidateClosedCandles} confirming candles
          </div>
        ) : null}
        {waitingSession ? (
          <div className="catchCompletion">Waiting for the trading session to be unlocked in Telegram.</div>
        ) : null}
        {catchTrade.lastError ? <div className="catchError">{catchTrade.lastError}</div> : null}
        {!catchTrade.active && catchTrade.completionReason ? (
          <div className="catchCompletion">{catchTrade.completionReason}</div>
        ) : null}
      </button>
      {attempts.length > 0 ? (
        <div className="catchAttempts">
          {attempts.map((attempt) => (
            <div key={attempt.id} className="catchAttemptRow">
              <strong>Attempt {attempt.attemptNumber || "-"}</strong>
              <span>{attempt.status}</span>
              <span>@ {formatTradePrice(attempt.actualEntry || attempt.plannedEntry)}</span>
              {tradePnl(attempt) != null ? <span>{formatPnl(tradePnl(attempt))}</span> : null}
            </div>
          ))}
        </div>
      ) : null}
      {catchTrade.active ? (
        <div className="tradeActions">
          {paused ? (
            <Button size="sm" variant="outline-primary" onClick={() => runCatchCommand(`resumeCatch c ${catchTrade.cur}`, null)}>
              Resume
            </Button>
          ) : null}
          <Button
            size="sm"
            variant="outline-danger"
            onClick={() => runCatchCommand(
              `cancelCatch c ${catchTrade.cur} confirm YES`,
              `Stop Catch supervision for ${catchTrade.symbol}? Any entered position and its existing orders will be left unchanged.`
            )}
          >
            Cancel Catch
          </Button>
        </div>
      ) : null}
    </div>
  );
}

function TradeCard({ trade, openPopup, onRefresh, livePrice, positionLane, positionOrders, positionFills, positionExecutions, exchangeClosed }) {
  const [editing, setEditing] = useState(false);
  const [comment, setComment] = useState(trade.comment || "");
  const active = isActiveTrade(trade);
  const exchangeRealized = exchangeRealizedPnl(positionExecutions, positionFills);
  const exchangeUnrealized = positionLane?.unrealizedPnl != null ? Number(positionLane.unrealizedPnl) : null;
  const realizedComponent = exchangeRealized ?? (active ? tradePnl(trade) : null);
  const exchangePnl = Number.isFinite(exchangeUnrealized)
    ? exchangeUnrealized + (realizedComponent ?? 0)
    : null;
  const pnl = active
    ? (Number.isFinite(exchangePnl) ? exchangePnl : activeTradePnl(trade, livePrice))
    : (exchangeRealized ?? tradePnl(trade));
  const isWin = pnl != null && pnl >= 0;
  const showPnlBreakdown = active && !exchangeClosed && (Number.isFinite(exchangeUnrealized) || realizedComponent != null);
  const amountText = tradeAmountText(trade);
  const slAmount = exchangeStopRiskAmount(trade, positionLane, positionOrders) ?? potentialStopLossAmount(trade);
  const exchangeText = exchangePositionText(positionLane);
  const closePrice = (!active || exchangeClosed)
    ? weightedClosePrice(positionExecutions, positionFills, trade.actualClose)
    : null;
  const displayRows = displayExecutions(positionExecutions, positionFills)
    .slice(-5)
    .reverse();

  const openTradeChart = () => {
    const event = buildTradeEvent(trade);
    const openedAt = toMillis(trade?.entryFilledAt) || tradeStartMillis(trade);
    if (openedAt != null && Date.now() - openedAt > 3 * 24 * 60 * 60 * 1000) {
      event.tf = "_4H";
    }
    openPopup({
      index: 0,
      sectionEvents: [event],
      sectionTitleWhenFromHistory: "My Trades",
      fullHistoryFlat: [event]
    });
  };

  const closePosition = () => {
    const command = closePositionCommand(trade);
    if (!active || !command) return;
    if (!window.confirm(`Close ${trade.side} ${trade.symbol} position at market?`)) return;
    processCommand({ command }).catch((error) => console.error("Close command failed:", error));
    setTimeout(onRefresh, 2000);
  };

  const reopenTrade = () => {
    const event = buildTradeEvent(trade);
    const tradeData = buildReopenTradeData(trade, livePrice);
    localStorage.setItem("tradePopup", "PRICE");
    openPopup({
      index: 0,
      sectionEvents: [event],
      sectionTitleWhenFromHistory: "My Trades",
      fullHistoryFlat: [event]
    });
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent("trade_window", { detail: tradeData }));
    }, 250);
  };

  const saveComment = () => {
    if (!active || !trade.processName) return;
    processCommand({
      command: `tradeComment p ${trade.processName} cm ${encodeCommandText(comment)} confirm YES`
    }).catch((error) => console.error("Comment command failed:", error));
    setEditing(false);
    setTimeout(onRefresh, 1500);
  };

  return (
    <div className={`tradeCard ${active && !exchangeClosed ? "tradeCardActive" : isWin ? "tradeCardWin" : "tradeCardLoss"}`}>
      <button type="button" className="tradeCardMain" onClick={openTradeChart}>
        <div className="tradeCardTop">
          <span className="tradeSymbol">
            <AssetLabel symbol={trade.symbol} event={trade} />
          </span>
          <span className={`tradeStatus ${active && !exchangeClosed ? "tradeStatusActive" : isWin ? "tradeStatusWin" : "tradeStatusLoss"}`}>
            {exchangeClosed ? "EXCHANGE CLOSED" : trade.status}
          </span>
        </div>
        <div className="tradeMeta">
          <span>{trade.side}</span>
          <span>{priceProtectLabel(trade)}</span>
          <span>{tradeEntryLabel(trade, positionLane?.entryPrice)}</span>
          {closePrice != null ? <span>Close {formatTradePrice(closePrice)}</span> : null}
          {active && (positionLane?.markPrice != null || livePrice != null) ? <span>Now {formatTradePrice(positionLane?.markPrice ?? livePrice)}</span> : null}
          <span>SL {formatTradePrice(trade.stopLoss)}</span>
        </div>
        {(amountText || exchangeText || slAmount != null) ? (
          <div className="tradeRiskMeta">
            {amountText ? <span>Initial {amountText}</span> : null}
            {exchangeText ? <span>Position {exchangeText}</span> : null}
            {slAmount != null ? <span>SL risk {formatPnl(-Math.abs(slAmount))}</span> : null}
          </div>
        ) : null}
        <div className="tradeMeta">{tpLabel(trade)}</div>
        {exchangeClosed ? <div className="tradeExchangeClosed">Exchange position closed</div> : null}
        {pnl != null ? (
          <div className={isWin ? "tradePnlWin" : "tradePnlLoss"}>
            {active && !exchangeClosed ? "Current PnL " : exchangeRealized != null ? "Exchange PnL " : ""}{formatPnl(pnl)}
          </div>
        ) : null}
        {showPnlBreakdown ? (
          <div className="tradePnlBreakdown">
            {Number.isFinite(exchangeUnrealized) ? <span>Unrealized {formatPnl(exchangeUnrealized)}</span> : null}
            {realizedComponent != null ? <span>Realized {formatPnl(realizedComponent)}</span> : null}
          </div>
        ) : null}
      </button>

      <div className="tradeActions">
        {active && !exchangeClosed ? (
          <Button size="sm" variant="outline-danger" onClick={closePosition}>
            Close Position
          </Button>
        ) : (
          <Button size="sm" variant="outline-primary" onClick={reopenTrade}>
            Reopen
          </Button>
        )}
      </div>

      <div className="tradeComment">
        {(positionOrders || []).length > 0 || displayRows.length > 0 ? (
          <div className="tradeExchangeMeta">
            {(positionOrders || []).slice(0, 3).map((order) => (
              <div key={`${order.orderId}-${order.type}`} className="tradeExchangeRow">
                <span>{String(order.type || "ORDER").replace("_MARKET", "")}</span>
                <span>{formatTradePrice(order.stopPrice || order.price)}</span>
              </div>
            ))}
            {displayRows.map((row) => (
              <div key={executionRowKey(row)} className="tradeExchangeRow">
                <span>{row.classification || row.side}{(row.fillsCount || row.fillCount) > 1 ? ` (${row.fillsCount || row.fillCount})` : ""}</span>
                <span>{formatTradePrice(executionRowPrice(row))}{executionPnlText(row)}</span>
              </div>
            ))}
          </div>
        ) : null}
        {editing ? (
          <>
            <Form.Control
              as="textarea"
              rows={6}
              value={comment}
              placeholder={TRADE_COMMENT_TEMPLATE}
              onChange={(e) => setComment(e.target.value)}
              className="tradeCommentInput"
            />
            <div className="tradeCommentActions">
              <Button size="sm" variant="secondary" onClick={() => setEditing(false)}>Cancel</Button>
              <Button size="sm" variant="primary" onClick={saveComment}>Save</Button>
            </div>
          </>
        ) : (
          <>
            {trade.comment ? <pre>{trade.comment}</pre> : <div className="tradeCommentEmpty">No journal note yet.</div>}
            {active && !exchangeClosed ? (
              <Button size="sm" variant="outline-primary" onClick={() => setEditing(true)}>
                Edit Comment
              </Button>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}

export default function MyTradesTab({ openPopup }) {
  const [showPnl, setShowPnl] = useState(false);
  const [showHistoryRange, setShowHistoryRange] = useState(false);
  const [historyRange, setHistoryRange] = useState(readHistoryRange);
  const tradesUrl = `${ORIGIN}/insights/account/trades`;
  const positionJournalUrl = `${ORIGIN}/insights/account/positions`;
  const { data, error, mutate, isValidating } = useSWR(tradesUrl, fetcher, {
    refreshInterval: 30000,
    revalidateOnFocus: false,
  });
  const { data: positionJournal, mutate: mutatePositionJournal } = useSWR(positionJournalUrl, fetcher, {
    refreshInterval: 20000,
    revalidateOnFocus: true,
  });
  const currentPositionJournal = positionJournal?.status === "CURRENT" ? positionJournal : null;

  const catches = useMemo(() => data?.catches || [], [data]);
  const trades = useMemo(() => data?.trades || [], [data]);
  const activeCatches = useMemo(() => catches.filter((item) => item.active), [catches]);
  const catchHistory = useMemo(() => catches.filter((item) => !item.active), [catches]);
  const decoratedTrades = useMemo(() => {
    return trades.map((trade) => {
      const positionLane = positionLaneForTrade(currentPositionJournal, trade);
      const positionOrders = positionOrdersForTrade(currentPositionJournal, trade);
      const positionFills = positionFillsForTrade(positionJournal, trade);
      const positionExecutions = positionExecutionsForTrade(positionJournal, trade);
      const hasExchangeEntry = trade.actualEntry != null
        || positionExecutions.some((execution) => ["OPEN", "ADD"].includes(execution.classification))
        || positionFills.some((fill) => ["OPEN", "ADD"].includes(fill.classification));
      const exchangeClosed = Boolean(currentPositionJournal) && isActiveTrade(trade) && hasExchangeEntry && !positionLane;
      return { trade, positionLane, positionOrders, positionFills, positionExecutions, exchangeClosed };
    });
  }, [currentPositionJournal, positionJournal, trades]);
  const active = useMemo(() => decoratedTrades.filter((item) => isActiveTrade(item.trade) && !item.exchangeClosed), [decoratedTrades]);
  const history = useMemo(() => decoratedTrades.filter((item) => !isActiveTrade(item.trade) || item.exchangeClosed), [decoratedTrades]);
  const executedHistory = useMemo(() => history.filter((item) => hasExecutedEntry(item.trade)), [history]);
  const cancelledHistory = useMemo(() => history.filter((item) => !hasExecutedEntry(item.trade)), [history]);
  const visibleExecutedHistory = useMemo(
    () => executedHistory.filter((item) => isInHistoryRange(tradeHistoryMillis(item), historyRange)),
    [executedHistory, historyRange]
  );
  const visibleCancelledHistory = useMemo(
    () => cancelledHistory.filter((item) => isInHistoryRange(tradeHistoryMillis(item), historyRange)),
    [cancelledHistory, historyRange]
  );
  const visibleCatchHistory = useMemo(
    () => catchHistory.filter((item) => isInHistoryRange(catchHistoryMillis(item), historyRange)),
    [catchHistory, historyRange]
  );
  const historyRangeLabel = HISTORY_RANGES.find(([, key]) => key === historyRange)?.[0] || "Last 7 days";
  const historyCount = visibleExecutedHistory.length + visibleCancelledHistory.length + visibleCatchHistory.length;
  const hasAnyHistory = executedHistory.length > 0 || cancelledHistory.length > 0 || catchHistory.length > 0;
  const pricedSymbols = useMemo(() => {
    return Array.from(new Set(
      trades
        .filter((trade) => trade.actualEntry != null && trade.initialQuantity != null)
        .map((trade) => String(trade.symbol || "").toUpperCase())
        .filter(Boolean)
    ));
  }, [trades]);
  const pricesUrl = pricedSymbols.length > 0
    ? `https://fapi.binance.com/fapi/v1/ticker/price?symbols=${encodeURIComponent(JSON.stringify(pricedSymbols))}`
    : null;
  const { data: pricesData } = useSWR(pricesUrl, fetcher, {
    refreshInterval: 10000,
    revalidateOnFocus: true,
  });
  const pricesBySymbol = useMemo(() => {
    const prices = {};
    const rows = Array.isArray(pricesData) ? pricesData : (pricesData ? [pricesData] : []);
    rows.forEach((item) => {
      const price = Number(item?.price);
      if (item?.symbol && Number.isFinite(price)) {
        prices[String(item.symbol).toUpperCase()] = price;
      }
    });
    return prices;
  }, [pricesData]);
  const refreshTrades = () => {
    mutate();
    mutatePositionJournal();
  };
  const selectHistoryRange = (value) => {
    setHistoryRange(value);
    setShowHistoryRange(false);
    try {
      localStorage.setItem(HISTORY_RANGE_STORAGE_KEY, value);
    } catch (error) {
      // The selected range still applies for this session when storage is unavailable.
    }
  };

  if (error) {
    return <div className="myTradesRoot"><div className="myTradesEmpty">Failed to load trades.</div></div>;
  }

  return (
    <div className="myTradesRoot">
      <div className="myTradesTop">
        <div>
          <div className="myTradesTitle">My Trades</div>
          <div className="myTradesSub">Auto-updates every 30s {isValidating ? "updating..." : ""}</div>
        </div>
        <div className="myTradesTopActions">
          <Button size="sm" variant="outline-info" onClick={() => setShowPnl(true)}>
            <FaChartLine aria-hidden="true" /> PnL
          </Button>
          <Button size="sm" variant="secondary" onClick={() => { mutate(); mutatePositionJournal(); }}>Refresh</Button>
        </div>
      </div>

      {positionJournal && !currentPositionJournal ? (
        <div className="myTradesEmpty" role="status">
          Exchange positions are temporarily unavailable. Trade status is shown from the local journal until a fresh exchange sync succeeds.
        </div>
      ) : null}

      <PnlSummaryModal show={showPnl} summary={data?.pnlSummary} onHide={() => setShowPnl(false)} />
      <HistoryRangeModal
        show={showHistoryRange}
        value={historyRange}
        onChange={selectHistoryRange}
        onHide={() => setShowHistoryRange(false)}
      />

      {!data ? <div className="myTradesEmpty">Loading trades...</div> : null}

      {activeCatches.length > 0 ? (
        <section className="myTradesSection myTradesSectionActiveCatch">
          <h3>Active Catch <span className="myTradesSectionCount">{activeCatches.length}</span></h3>
          <div className="myTradesGrid">
            {activeCatches.map((catchTrade) => (
              <CatchCard key={catchTrade.id} catchTrade={catchTrade} openPopup={openPopup} onRefresh={refreshTrades} />
            ))}
          </div>
        </section>
      ) : null}

      {active.length > 0 ? (
        <section className="myTradesSection myTradesSectionActive">
          <h3>Active <span className="myTradesSectionCount">{active.length}</span></h3>
          <div className="myTradesGrid">
            {active.map(({ trade, positionLane, positionOrders, positionFills, positionExecutions, exchangeClosed }) => (
              <TradeCard
                key={trade.id}
                trade={trade}
                openPopup={openPopup}
                onRefresh={refreshTrades}
                livePrice={pricesBySymbol[String(trade.symbol || "").toUpperCase()]}
                positionLane={positionLane}
                positionOrders={positionOrders}
                positionFills={positionFills}
                positionExecutions={positionExecutions}
                exchangeClosed={exchangeClosed}
              />
            ))}
          </div>
        </section>
      ) : null}

      {hasAnyHistory ? (
        <div className="myTradesHistoryToolbar">
          <div className="myTradesHistoryHeading">
            <h3>History</h3>
            <span>{historyCount}</span>
          </div>
          <Button size="sm" variant="outline-secondary" onClick={() => setShowHistoryRange(true)}>
            <FaCalendarAlt aria-hidden="true" /> {historyRangeLabel}
          </Button>
        </div>
      ) : null}

      {visibleExecutedHistory.length > 0 ? (
        <section className="myTradesSection myTradesSectionExecuted">
          <h3>Executed <span className="myTradesSectionCount">{visibleExecutedHistory.length}</span></h3>
          <div className="myTradesGrid">
            {visibleExecutedHistory.map(({ trade, positionLane, positionOrders, positionFills, positionExecutions, exchangeClosed }) => (
              <TradeCard
                key={trade.id}
                trade={trade}
                openPopup={openPopup}
                onRefresh={refreshTrades}
                positionLane={positionLane}
                positionOrders={positionOrders}
                positionFills={positionFills}
                positionExecutions={positionExecutions}
                exchangeClosed={exchangeClosed}
              />
            ))}
          </div>
        </section>
      ) : null}

      {visibleCancelledHistory.length > 0 ? (
        <section className="myTradesSection myTradesSectionCancelled">
          <h3>Cancelled <span className="myTradesSectionCount">{visibleCancelledHistory.length}</span></h3>
          <div className="myTradesGrid">
            {visibleCancelledHistory.map(({ trade, positionLane, positionOrders, positionFills, positionExecutions, exchangeClosed }) => (
              <TradeCard
                key={trade.id}
                trade={trade}
                openPopup={openPopup}
                onRefresh={refreshTrades}
                positionLane={positionLane}
                positionOrders={positionOrders}
                positionFills={positionFills}
                positionExecutions={positionExecutions}
                exchangeClosed={exchangeClosed}
              />
            ))}
          </div>
        </section>
      ) : null}

      {visibleCatchHistory.length > 0 ? (
        <section className="myTradesSection myTradesSectionCatch">
          <h3>Catch <span className="myTradesSectionCount">{visibleCatchHistory.length}</span></h3>
          <div className="myTradesGrid">
            {visibleCatchHistory.map((catchTrade) => (
              <CatchCard key={catchTrade.id} catchTrade={catchTrade} openPopup={openPopup} onRefresh={refreshTrades} />
            ))}
          </div>
        </section>
      ) : null}

      {hasAnyHistory && historyCount === 0 ? (
        <div className="myTradesEmpty">No completed trades in {historyRangeLabel.toLowerCase()}.</div>
      ) : null}

      {data && trades.length === 0 && catches.length === 0 ? <div className="myTradesEmpty">No trades synced yet.</div> : null}
    </div>
  );
}
